import type { AccountDetails, BillingDetails, SupportedPaymentNetworks } from '@klear/bank-account/schema'
import {
  type PaymentAccountType,
  PAYMENT_ACCOUNT_TYPE_KLEAR_FINANCIAL_ACCOUNT,
  PAYMENT_ACCOUNT_TYPE_EXTERNAL_FINANCIAL_ACCOUNT,
  PAYMENT_ACCOUNT_TYPE_PLACEHOLDER,
  type PaymentNetwork,
  PAYMENT_NETWORK_TYPES
} from '@klear/payment/constants'

export interface ExternalBankAccountForPayment {
  id?: number
  accountDetails: AccountDetails
  billingDetails: BillingDetails
  supportedPaymentNetworks: SupportedPaymentNetworks
  name: string
  last4: string
  accountNumber: string
  routingNumber: string
}

export interface TraderExternalBankAccountForPayment extends ExternalBankAccountForPayment {
  achSignedDocumentKey?: string
}

export interface PaymentAccountOrganization {
  id: number
  name: string
  stripeAccountId?: string | null
}

export interface KlearFinancialAccountForPayment {
  id?: number
  financialAccountId: string
  name: string
  last4?: string | null
  accountNumber?: string | null
  routingNumber?: string | null
  signedDocumentKey?: string | null
  organization: PaymentAccountOrganization
}

export class PaymentAccount {
  id: number
  stripeAccountId: string
  financialAccountId: string
  type: PaymentAccountType
  isKlearAccount: boolean
  isExternalAccount: boolean
  accountDetails: AccountDetails | Record<string, never>
  billingDetails: BillingDetails | Record<string, never>
  name: string
  last4: string
  accountNumber: string
  routingNumber: string
  supportedPaymentNetworks: PaymentNetwork[]
  organizationId: number
  organizationName: string
  achAgreementSignedDocumentKey: string | null

  constructor(paymentAccount: PaymentAccount) {
    this.id = paymentAccount.id
    this.stripeAccountId = paymentAccount.stripeAccountId
    this.financialAccountId = paymentAccount.financialAccountId
    this.type = paymentAccount.type
    this.isKlearAccount = paymentAccount.isKlearAccount
    this.isExternalAccount = paymentAccount.isExternalAccount
    this.accountDetails = paymentAccount.accountDetails
    this.billingDetails = paymentAccount.billingDetails
    this.name = paymentAccount.name
    this.last4 = paymentAccount.last4
    this.accountNumber = paymentAccount.accountNumber
    this.routingNumber = paymentAccount.routingNumber
    this.supportedPaymentNetworks = paymentAccount.supportedPaymentNetworks
    this.organizationId = paymentAccount.organizationId
    this.organizationName = paymentAccount.organizationName
    this.achAgreementSignedDocumentKey = paymentAccount.achAgreementSignedDocumentKey ?? null
  }

  static fromKlearFinancialAccount(klearFinancialAccount: KlearFinancialAccountForPayment): PaymentAccount {
    return new PaymentAccount({
      id: klearFinancialAccount.id ?? 0,
      stripeAccountId: klearFinancialAccount.organization.stripeAccountId ?? '',
      financialAccountId: klearFinancialAccount.financialAccountId,
      type: PAYMENT_ACCOUNT_TYPE_KLEAR_FINANCIAL_ACCOUNT,
      isKlearAccount: true,
      isExternalAccount: false,
      accountDetails: {},
      billingDetails: {},
      name: klearFinancialAccount.name,
      last4: klearFinancialAccount.last4 ?? '',
      accountNumber: klearFinancialAccount.accountNumber ?? '',
      routingNumber: klearFinancialAccount.routingNumber ?? '',
      supportedPaymentNetworks: [...PAYMENT_NETWORK_TYPES],
      organizationId: klearFinancialAccount.organization.id,
      organizationName: klearFinancialAccount.organization.name,
      achAgreementSignedDocumentKey: klearFinancialAccount.signedDocumentKey ?? null
    })
  }

  static placeholder(organization: PaymentAccountOrganization): PaymentAccount {
    return new PaymentAccount({
      id: 0,
      stripeAccountId: '',
      financialAccountId: '',
      type: PAYMENT_ACCOUNT_TYPE_PLACEHOLDER,
      isKlearAccount: false,
      isExternalAccount: false,
      accountDetails: {},
      billingDetails: {},
      name: 'Choose an account',
      last4: '',
      accountNumber: '',
      routingNumber: '',
      supportedPaymentNetworks: [],
      organizationId: organization.id,
      organizationName: organization.name,
      achAgreementSignedDocumentKey: null
    })
  }

  static fromTraderExternalBankAccount(
    account: TraderExternalBankAccountForPayment,
    organization: PaymentAccountOrganization
  ): PaymentAccount {
    return PaymentAccount.fromExternalBankAccount(account, organization, account.achSignedDocumentKey ?? null)
  }

  static fromSupplierExternalBankAccount(
    account: ExternalBankAccountForPayment,
    organization: PaymentAccountOrganization
  ): PaymentAccount {
    return PaymentAccount.fromExternalBankAccount(account, organization, null)
  }

  private static fromExternalBankAccount(
    account: ExternalBankAccountForPayment,
    organization: PaymentAccountOrganization,
    achAgreementSignedDocumentKey: string | null
  ): PaymentAccount {
    if (account.id == null) {
      throw new Error('PaymentAccount.fromExternalBankAccount requires a saved account (missing id)')
    }
    return new PaymentAccount({
      id: account.id,
      stripeAccountId: organization.stripeAccountId ?? '',
      financialAccountId: '',
      type: PAYMENT_ACCOUNT_TYPE_EXTERNAL_FINANCIAL_ACCOUNT,
      isKlearAccount: false,
      isExternalAccount: true,
      accountDetails: account.accountDetails,
      billingDetails: account.billingDetails,
      name: account.name,
      last4: account.last4,
      accountNumber: account.accountNumber,
      routingNumber: account.routingNumber,
      supportedPaymentNetworks: account.supportedPaymentNetworks as PaymentNetwork[],
      organizationId: organization.id,
      organizationName: organization.name,
      achAgreementSignedDocumentKey
    })
  }
}

export function accountFields(
  relation: 'source',
  account: PaymentAccount
): {
  sourceAccountType: PaymentAccountType
  sourceAccountId: number
  sourceAccountOrganizationId: number
  sourceAccountOrganizationName: string
}
export function accountFields(
  relation: 'destination',
  account: PaymentAccount
): {
  destinationAccountType: PaymentAccountType
  destinationAccountId: number
  destinationAccountOrganizationId: number
  destinationAccountOrganizationName: string
}
export function accountFields(relation: 'source' | 'destination', account: PaymentAccount) {
  return {
    [relation === 'source' ? 'sourceAccountType' : 'destinationAccountType']: account.type,
    [relation === 'source' ? 'sourceAccountId' : 'destinationAccountId']: account.id,
    [relation === 'source' ? 'sourceAccountOrganizationId' : 'destinationAccountOrganizationId']:
      account.organizationId,
    [relation === 'source' ? 'sourceAccountOrganizationName' : 'destinationAccountOrganizationName']:
      account.organizationName
  }
}
